import { useWallet } from "@solana/wallet-adapter-react";
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    useRef,
    useContext,
} from "react";
import { v4 as uuid } from "uuid";
import jwt_decode from "jwt-decode";
import bs58 from "bs58";

import { GlobalContext } from "../Contexts/GlobalContext";
import { AuthContext } from "../Contexts/useAuth";
import { doSolanaAuth } from "../Services/ApiClient";

import config from "../config";

const msgTpl = config["SolanaAuthTpl"];
const provider = process.env.REACT_APP_SOLANA_NETWORK_PROVIDER;
const CHECK_EXPIRED_INTERVAL = 10 * 1000; // 10 seconds

function loadLocalAuth(solanaPubKey) {
    const itemKey = `auth-${solanaPubKey}`;
    try {
        const value = localStorage.getItem(itemKey);
        if (value) {
            let auth = JSON.parse(value);
            let oneDayLater = Date.now() + 86400000;
            if (auth.expiredAt > oneDayLater) {
                // Be at least 1 day to live
                return auth;
            } else {
                localStorage.removeItem(itemKey);
            }
        }
    } catch (error) {
        if (window !== undefined) {
            console.error(error);
        }
    }
    return null;
}

function saveToLocalAuth(solanaPubKey, auth) {
    const itemKey = `auth-${solanaPubKey}`;
    try {
        const decoded = jwt_decode(auth.jwt);
        auth.expiredAt = decoded.exp * 1000;
        const value = JSON.stringify(auth);
        localStorage.setItem(itemKey, value);
    } catch (error) {
        if (window !== undefined) {
            console.error(error);
        }
    }
}

function updateLoacalAuth(solanaPubKey, updated) {
    const itemKey = `auth-${solanaPubKey}`;
    try {
        const value = JSON.stringify(updated);
        localStorage.setItem(itemKey, value);
    } catch (error) {
        if (window !== undefined) {
            console.error(error);
        }
    }
}

function clearLocalAuth(solanaPubKey) {
    const itemKey = `auth-${solanaPubKey}`;
    try {
        localStorage.removeItem(itemKey);
    } catch (error) {
        if (window !== undefined) {
            console.error(error);
        }
    }
}

export function AuthProvider({ children }) {
    const { changeConnectionConfig } = useContext(GlobalContext);
    const { publicKey, signMessage, disconnect } = useWallet();
    const pkBase58 = useMemo(() => publicKey?.toBase58() || "", [publicKey]);
    const localAuth = useMemo(() => {
        if (pkBase58) {
            return loadLocalAuth(pkBase58);
        }
        return null;
    }, [pkBase58]);

    const [authed, setAuthed] = useState(false);
    const [jwt, setJwt] = useState(localAuth?.jwt || "");
    const [verifiedPolygonAccts, setVerifiedPolygonAccts] = useState(
        localAuth?.verifiedPolygonAccts || []
    );

    // Monitor Wallet Change
    const prevPkBase58 = useRef(pkBase58);
    useEffect(() => {
        console.log("Prev Auth Info", prevPkBase58.current);
        console.log("Current Auth Info", pkBase58);
        if (prevPkBase58.current === pkBase58) {
            return;
        }
        if (prevPkBase58.current === "") {
            prevPkBase58.current = pkBase58;
            if (localAuth) {
                setAuthed(true);
                setJwt(localAuth.jwt);
                setVerifiedPolygonAccts(localAuth.verifiedPolygonAccts);
            }
        } else {
            disconnect();
            prevPkBase58.current = "";
            setAuthed(false);
            setJwt("");
            setVerifiedPolygonAccts([]);
        }
    }, [localAuth, pkBase58, disconnect]);

    // Monitor JWT Expiration
    useEffect(() => {
        const localAuth = loadLocalAuth(pkBase58);
        if (localAuth === null) {
            return;
        }
        const interval = setInterval(() => {
            const expiredAt = localAuth.expiredAt;
            const now = Date.now();
            if (expiredAt < now) {
                setAuthed(false);
                setJwt("");
                setVerifiedPolygonAccts([]);
                clearLocalAuth(pkBase58);
            }
        }, CHECK_EXPIRED_INTERVAL);
        return () => {
            clearInterval(interval);
        };
    }, [pkBase58]);

    // Change Connection Config
    useEffect(() => {
        if (provider !== "solana") {
            changeConnectionConfig(jwt);
        }
    }, [jwt, changeConnectionConfig]);

    const doAuth = useCallback(async () => {
        // Not Connected, should not happen.
        if (!signMessage) {
            return false;
        }
        // Already Authed
        if (localAuth) {
            return true;
        }
        const messageId = uuid();
        const message = Buffer.from(
            msgTpl.replace("{{MESSAGE_ID}}", messageId)
        );
        let signature;
        let auth;
        try {
            signature = bs58.encode(await signMessage(message));
            auth = await doSolanaAuth(pkBase58, signature, messageId);
        } catch (error) {
            setAuthed(false);
            setJwt("");
            setVerifiedPolygonAccts([]);
            clearLocalAuth(pkBase58);
            disconnect();
            throw error;
        }
        setAuthed(true);
        setJwt(auth.jwt);
        setVerifiedPolygonAccts(auth.verifiedPolygonAccts);
        saveToLocalAuth(pkBase58, auth);

        return true;
    }, [localAuth, pkBase58, signMessage, disconnect]);

    const saveVerifedPolygonAcct = useCallback(
        (polygonAcct) => {
            const localAuth = loadLocalAuth(pkBase58);
            if (localAuth === null) {
                return;
            }
            if (localAuth.verifiedPolygonAccts.indexOf(polygonAcct) >= 0) {
                return false;
            } else {
                localAuth.verifiedPolygonAccts.push(polygonAcct);
                setVerifiedPolygonAccts([...localAuth.verifiedPolygonAccts]);
                updateLoacalAuth(pkBase58, localAuth);
                return true;
            }
        },
        [pkBase58]
    );

    return (
        <AuthContext.Provider
            value={{
                authed,
                solanaPubKey: pkBase58,
                jwt,
                verifiedPolygonAccts,
                saveVerifedPolygonAcct,
                doAuth,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
